import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Suspense, useCallback, useState} from 'react'
import {ForYouSubSection} from './shared/ForYouSubSection'
import {
  getExpandedSubSections,
  type ForYouSubSectionId,
  type ForYouSubSectionState,
  type ForYouSubSectionVisibility,
} from './shared/pick-expanded-section'
import {RequiredReviewer} from './mergeability/RequiredReviewer'
import {ApprovableChecks} from './mergeability/ApprovableChecks'
import {AwaitingApprovalChecks} from './mergeability/AwaitingApprovalChecks'
import {MergeConflicts} from './mergeability/MergeConflicts'
import {FailingChecks} from './mergeability/FailingChecks'
import {DraftPR} from './mergeability/DraftPR'
import {ReadyToMerge} from './mergeability/ReadyToMerge'
import {Findings} from './findings/Findings'
import {Deployments} from './deployments/Deployments'
import {ReviewRequested} from './reviewer/ReviewRequested'
import {ReviewPending} from './reviewer/ReviewPending'
import {ChangesSinceLastReview} from './reviewer/ChangesSinceLastReview'
import {ForYouSectionError, ForYouSectionSkeleton} from './ForYouSectionFallbacks'
import {ForYouItemBoundary} from './shared/ForYouItemBoundary'
import {SyncCommentData} from './commenting/shared/SyncCommentData'
import {CommentsAndReviews} from './commenting/CommentsAndReviews'
import {MergeBlocked} from './mergeability/MergeBlocked'

const EMPTY_SUBSECTION_STATE: ForYouSubSectionState = {hasVisibleItems: false, hasLoadingItems: false}

const DEFAULT_SUBSECTION_VISIBILITY: Partial<Record<ForYouSubSectionId, boolean>> = {
  comments: true,
}

export function ForYouSection() {
  return (
    <ErrorBoundary boundaryName="ForYouSection" fallback={<ForYouSectionError />}>
      <Suspense fallback={<ForYouSectionSkeleton />}>
        <ForYouSectionContent />
      </Suspense>
    </ErrorBoundary>
  )
}

function ForYouSectionContent() {
  // Which subsections currently have confirmed visible items and/or loading
  // placeholders. Fed by each subsection's `onVisibilityChange`.
  const [visibility, setVisibility] = useState<ForYouSubSectionVisibility>({
    needsAttention: EMPTY_SUBSECTION_STATE,
    relevant: EMPTY_SUBSECTION_STATE,
    comments: EMPTY_SUBSECTION_STATE,
  })

  // Sections the user has explicitly toggled; these always win over the priority recommendation.
  // Comments are always expanded by default, so we start with that override.
  const [userOverrides, setUserOverrides] =
    useState<Partial<Record<ForYouSubSectionId, boolean>>>(DEFAULT_SUBSECTION_VISIBILITY)

  const expanded = getExpandedSubSections(visibility, userOverrides)

  const handleToggle = useCallback(
    (id: ForYouSubSectionId, currentlyExpanded: boolean) =>
      setUserOverrides(prev => ({...prev, [id]: !currentlyExpanded})),
    [],
  )

  const handleVisibilityChange = useCallback(
    (id: ForYouSubSectionId, state: ForYouSubSectionState) =>
      setVisibility(prev => updateSubSectionVisibility(prev, id, state)),
    [],
  )

  return (
    <div className="d-flex gap-3 flex-column">
      <SyncCommentData />

      <NeedsAttentionSection
        expanded={expanded.needsAttention}
        onToggle={handleToggle}
        onVisibilityChange={handleVisibilityChange}
      />
      <RelevantToYouSection
        expanded={expanded.relevant}
        onToggle={handleToggle}
        onVisibilityChange={handleVisibilityChange}
      />
      <ConversationSection
        expanded={expanded.comments}
        onToggle={handleToggle}
        onVisibilityChange={handleVisibilityChange}
      />
    </div>
  )
}

function updateSubSectionVisibility(
  visibility: ForYouSubSectionVisibility,
  section: ForYouSubSectionId,
  nextState: ForYouSubSectionState,
) {
  const currentState = visibility[section]
  if (
    currentState.hasVisibleItems === nextState.hasVisibleItems &&
    currentState.hasLoadingItems === nextState.hasLoadingItems
  ) {
    return visibility
  }

  return {...visibility, [section]: nextState}
}

interface SubSectionProps {
  expanded: boolean
  onToggle: (id: ForYouSubSectionId, currentlyExpanded: boolean) => void
  onVisibilityChange: (id: ForYouSubSectionId, state: ForYouSubSectionState) => void
}

function NeedsAttentionSection({expanded, onToggle, onVisibilityChange}: SubSectionProps) {
  return (
    <ForYouSubSection
      id="needsAttention"
      title="Needs attention"
      expanded={expanded}
      onToggle={onToggle}
      onVisibilityChange={onVisibilityChange}
    >
      <ForYouItemBoundary name="ReviewPending">
        <ReviewPending />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ReviewRequested">
        <ReviewRequested />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="MergeConflicts">
        <MergeConflicts />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="FailingChecks" audience="author">
        <FailingChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ApprovableChecks">
        <ApprovableChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="MergeBlocked" audience="author">
        <MergeBlocked />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="Findings">
        <Findings />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="DraftPR" audience="author">
        <DraftPR />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ReadyToMerge" audience="author">
        <ReadyToMerge />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ChangesSinceLastReview">
        <ChangesSinceLastReview />
      </ForYouItemBoundary>
    </ForYouSubSection>
  )
}

function RelevantToYouSection({expanded, onToggle, onVisibilityChange}: SubSectionProps) {
  return (
    <ForYouSubSection
      id="relevant"
      title="Relevant"
      expanded={expanded}
      onToggle={onToggle}
      onVisibilityChange={onVisibilityChange}
    >
      <ForYouItemBoundary name="Checks">
        <AwaitingApprovalChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="RequiredReviewer">
        <RequiredReviewer />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="FailingChecks" audience="reviewer">
        <FailingChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="MergeBlocked" audience="reviewer">
        <MergeBlocked />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="Deployments">
        <Deployments />
      </ForYouItemBoundary>
    </ForYouSubSection>
  )
}

// This won't be the final home for Comments but a step in the right direction without a full refactor.
// The items within the section still aren't ordered consistently and some may need refactored to be resurfaced in
// Needs Attention (ie - mentioned or replied to comments) but this is a good first step to get all the commenting items into one section.
function ConversationSection({expanded, onToggle, onVisibilityChange}: SubSectionProps) {
  return (
    <ForYouSubSection
      id="comments"
      title="Conversation"
      expanded={expanded}
      onToggle={onToggle}
      onVisibilityChange={onVisibilityChange}
    >
      <ForYouItemBoundary name="Conversation">
        <CommentsAndReviews />
      </ForYouItemBoundary>
    </ForYouSubSection>
  )
}
